import { ErrorModal, MesfModal, useShiftNavigator } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, get, useForm } from "react-hook-form";
import { HeatFormData, INITIAL_VALUES } from "../../models/Initial-values";
import { upsertHeat } from "../../repositories/heatsRepository";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { Heat } from "@/pages/meltshop/models/Heat";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { LadleSelector } from "@/shared/selectors/LadleSelector";

type Props = {
  show: boolean;
  onHide: (shouldUpdate: boolean) => void;
  heatsByShift: Heat[];
};

export const CreateHeatModal = ({ show, onHide, heatsByShift }: Props) => {
  const { shiftInfo } = useShiftNavigator();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit, reset } = useForm<HeatFormData>({
    defaultValues: INITIAL_VALUES,
  });

  const onSubmit: SubmitHandler<HeatFormData> = async (data) => {
    setIsSubmitLoading(true);

    try {
      if (heatsByShift && heatsByShift.length > 0) {
        if (
          heatsByShift.some(
            (heat) =>
              heat.HeatName?.toLowerCase() === data.HeatName.toLowerCase(),
          )
        ) {
          setError("A Heat with this name already exists");
          return;
        }
      }
      const res = await upsertHeat(null, {
        ...data,
        ShiftId: get(shiftInfo, "CurrentShiftId", null),
      });

      if (res.ok) {
        setNotificationMessage("The heat was created successfully");
        onHide(true);
      } else {
        setError(res.message);
      }
    } catch (error: any) {
      console.error("An unexpected error occurred:", error);
      setError(error.message || "An unexpected error occurred");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      reset();
    }
  }, [show]);

  return (
    <>
      <>
        <MesfModal
          open={show}
          handleClose={() => {
            onHide(true);
          }}
          id="editHeat"
          title="Create a heat"
          maxWidth="sm"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <MesfModal.Content style={{ padding: "15px 30px" }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="HeatName"
                    rules={{
                      required: "Name is required",
                      pattern: {
                        value: /^.{6}$/,
                        message: "Name must be 6 characters long",
                      },
                    }}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Name"
                        value={field.value}
                        onChange={field.onChange}
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        margin="dense"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="GradeId"
                    control={control}
                    render={({ field }) => (
                      <GradeSelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="SteelWeight"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Steel Weight"
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          step: 0.1,
                          min: 0.1,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="LadleId"
                    control={control}
                    render={({ field }) => (
                      <LadleSelector
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MesfModal.Content>
            <MesfModal.Actions style={{ padding: "2rem" }}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item md={3} xs={12} style={{ margin: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="inherit"
                    onClick={() => {
                      onHide(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item md={3} xs={12} style={{ margin: 0 }}>
                  <Button
                    fullWidth
                    startIcon={
                      isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
                    }
                    disabled={isSubmitLoading}
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    Create
                  </Button>
                </Grid>
              </Grid>
            </MesfModal.Actions>
          </form>
        </MesfModal>
      </>
      <ErrorModal onHide={() => setError(null)} error={error} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
